import { useEffect, useState } from "react";
import { BarChart3, ChevronDown, Clock, Columns3, Download, List, Loader2, Mail, Phone, Plus, Target, Trophy, UserCheck, UserRound, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const STAGE_COLORS = {
  new: "bg-blue-100 text-blue-700",
  contacted: "bg-amber-100 text-amber-700",
  viewing: "bg-violet-100 text-violet-700",
  negotiating: "bg-orange-100 text-orange-700",
  won: "bg-emerald-100 text-emerald-700",
  lost: "bg-stone-200 text-stone-600",
};
const STAGE_BAR = {
  new: "bg-blue-400",
  contacted: "bg-amber-400",
  viewing: "bg-violet-400",
  negotiating: "bg-orange-400",
  won: "bg-emerald-500",
  lost: "bg-stone-400",
};

const fmt = (d) => (d ? new Date(d).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

const chips = (l) => [["intent", l.intent], ["area", l.community], ["budget", l.budget], ["beds", l.bedrooms], ["timeline", l.timeline]].filter(([, v]) => v);

const LeadDetail = ({ lead, agents, onChange }) => {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const patch = async (body) => {
    setBusy(true);
    try { await api.patch(`/admin/realestate/leads/${lead.id}`, body); await onChange(); }
    catch { toast.error("Update failed"); }
    finally { setBusy(false); }
  };
  const addNote = async () => {
    if (!note.trim()) return;
    await patch({ note: note.trim() });
    setNote("");
    toast.success("Note added");
  };

  return (
    <div className="mt-4 grid gap-5 border-t border-[#EBE3DA] pt-4 lg:grid-cols-2" data-testid={`re-lead-detail-${lead.id}`}>
      <div className="space-y-4">
        <div>
          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-[#B38B2D]">Assign to agent</p>
          <select
            value={lead.assigned_agent_id || ""}
            onChange={(e) => patch({ assigned_agent_id: e.target.value })}
            disabled={busy}
            data-testid={`re-lead-assign-${lead.id}`}
            className="input-lux text-sm"
          >
            <option value="">— Unassigned —</option>
            {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>
        <div>
          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-[#B38B2D]">Notes</p>
          <div className="space-y-2">
            {(lead.notes || []).map((n, i) => (
              <div key={i} className="rounded-lg bg-white/70 px-3 py-2 text-xs">
                <p className="text-[#1A1615]">{n.text}</p>
                <p className="mt-0.5 text-[10px] text-[#91837C]">{n.by ? `${n.by} · ` : ""}{fmt(n.at)}</p>
              </div>
            ))}
            {!(lead.notes || []).length && <p className="text-xs font-light text-[#91837C]">No notes yet.</p>}
          </div>
          <div className="mt-3 flex gap-2">
            <input className="input-lux flex-1 text-sm" placeholder="Add a note…" value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addNote()} data-testid={`re-lead-note-input-${lead.id}`} />
            <button onClick={addNote} disabled={busy || !note.trim()} data-testid={`re-lead-note-add-${lead.id}`} className="btn-gold inline-flex items-center gap-1.5 px-4 py-2.5 text-[11px] disabled:opacity-60">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Add
            </button>
          </div>
        </div>
      </div>
      <div>
        <p className="mb-1.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-[#B38B2D]"><Clock className="h-3 w-3" /> Activity timeline</p>
        <ol className="relative space-y-3 border-l border-[#EBE3DA] pl-4">
          {[...(lead.activity || [])].reverse().map((a, i) => (
            <li key={i} className="relative">
              <span className="absolute -left-[21px] top-1 h-2.5 w-2.5 rounded-full bg-[#C9A24B]" />
              <p className="text-xs text-[#1A1615]">{a.detail}</p>
              <p className="text-[10px] text-[#91837C]">{a.by ? `${a.by} · ` : ""}{fmt(a.at)}</p>
            </li>
          ))}
          {!(lead.activity || []).length && <li className="text-xs font-light text-[#91837C]">No activity yet — stage changes, assignments and notes appear here.</li>}
        </ol>
      </div>
    </div>
  );
};

const NeedsHumanTag = () => (
  <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.1em] text-rose-700" data-testid="re-lead-needs-human">
    <UserRound className="h-2.5 w-2.5" /> Needs human
  </span>
);

const OutcomeTag = ({ stage, reason }) => (
  <p className={`mt-1.5 inline-flex max-w-full items-center gap-1 truncate rounded-md px-2 py-0.5 text-[10px] font-medium ${stage === "won" ? "bg-emerald-50 text-emerald-700" : "bg-stone-100 text-stone-600"}`}>
    {stage === "won" ? "✓ Won" : "✕ Lost"}{reason ? ` · ${reason}` : ""}
  </p>
);

const WON_REASONS = ["Price fit", "Right location", "Payment plan", "Good ROI", "Fast decision", "Trusted us"];
const LOST_REASONS = ["Budget too high", "Chose competitor", "Timing not right", "Went unresponsive", "Location mismatch", "Changed mind"];

const ReasonModal = ({ stage, onSave, onClose }) => {
  const [reason, setReason] = useState("");
  const presets = stage === "won" ? WON_REASONS : LOST_REASONS;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={onClose} data-testid="re-lead-reason-modal">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <p className="flex items-center gap-2 font-serif text-lg font-semibold text-[#1A1615]">
              <Trophy className={`h-4 w-4 ${stage === "won" ? "text-emerald-600" : "text-stone-400"}`} />
              Why was this lead {stage === "won" ? "won" : "lost"}?
            </p>
            <p className="mt-1 text-xs text-[#91837C]">A quick reason helps the team learn what closes deals.</p>
          </div>
          <button onClick={onClose} aria-label="Close" className="flex h-8 w-8 items-center justify-center rounded-full text-[#C9B8A8] hover:text-[#1A1615]"><X className="h-4 w-4" /></button>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {presets.map((p) => (
            <button key={p} onClick={() => setReason(p)} data-testid={`re-reason-preset-${p.replace(/\s+/g, "-").toLowerCase()}`}
              className={`rounded-full px-3 py-1.5 text-[11px] font-medium transition-colors ${reason === p ? "bg-[#1A1615] text-[#F3E5AB]" : "border border-[#E5D9CF] text-[#5A504B] hover:bg-[#F5EBE6]"}`}>
              {p}
            </button>
          ))}
        </div>
        <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Or type your own reason…" data-testid="re-reason-input"
          className="input-lux mt-4 w-full text-sm" autoFocus onKeyDown={(e) => e.key === "Enter" && onSave(reason.trim())} />
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={() => onSave("")} data-testid="re-reason-skip" className="rounded-full border border-[#E5D9CF] px-4 py-2 text-xs font-medium text-[#5A504B] hover:bg-[#F5EBE6]">Skip</button>
          <button onClick={() => onSave(reason.trim())} data-testid="re-reason-save" className="btn-gold px-5 py-2 text-xs font-semibold">Save & move</button>
        </div>
      </div>
    </div>
  );
};

/* ---------------- Kanban board (drag & drop) ---------------- */
const Board = ({ leads, stages, agents, onStage, onChange }) => {
  const [dragId, setDragId] = useState(null);
  const [over, setOver] = useState(null);
  const [open, setOpen] = useState(null);

  const drop = (stage) => {
    if (dragId) onStage(dragId, stage);
    setDragId(null); setOver(null);
  };

  return (
    <div className="mt-5 flex gap-4 overflow-x-auto pb-4" data-testid="re-leads-board">
      {stages.map((s) => {
        const col = leads.filter((l) => l.stage === s);
        return (
          <div
            key={s}
            onDragOver={(e) => { e.preventDefault(); setOver(s); }}
            onDragLeave={() => setOver((o) => (o === s ? null : o))}
            onDrop={() => drop(s)}
            data-testid={`re-board-col-${s}`}
            className={`flex w-[270px] flex-shrink-0 flex-col rounded-2xl border p-2.5 transition-colors ${over === s ? "border-[#C9A24B] bg-[#FBF6EA]" : "border-[#EBE3DA] bg-white/50"}`}
          >
            <div className="mb-2 flex items-center justify-between px-1">
              <div className="flex items-center gap-2">
                <span className={`h-2 w-2 rounded-full ${STAGE_BAR[s] || "bg-stone-400"}`} />
                <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-[#1A1615]">{s}</span>
              </div>
              <span className="rounded-full bg-[#F5EBE6] px-2 py-0.5 text-[10px] font-semibold text-[#5A504B]">{col.length}</span>
            </div>
            <div className="flex flex-col gap-2">
              {col.map((l) => (
                <div
                  key={l.id}
                  draggable
                  onDragStart={() => setDragId(l.id)}
                  onDragEnd={() => { setDragId(null); setOver(null); }}
                  data-testid={`re-board-card-${l.id}`}
                  className={`cursor-grab rounded-xl border border-[#EBE3DA] bg-white p-3 shadow-sm transition-all hover:shadow-md active:cursor-grabbing ${dragId === l.id ? "opacity-40" : ""}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-semibold text-[#1A1615]">{l.name}</p>
                    {l.needs_human && <NeedsHumanTag />}
                  </div>
                  {l.phone && <p className="mt-0.5 text-[11px] text-[#91837C]">{l.phone}</p>}
                  <div className="mt-2 flex flex-wrap gap-1">
                    {chips(l).slice(0, 3).map(([k, v]) => (
                      <span key={k} className="rounded-full bg-[#F8F3E6] px-2 py-0.5 text-[10px] text-[#5A504B]"><b className="text-[#1A1615]">{k}</b> {String(v)}</span>
                    ))}
                  </div>
                  {(l.stage === "won" || l.stage === "lost") && <OutcomeTag stage={l.stage} reason={l.outcome_reason} />}
                  <div className="mt-2 flex items-center justify-between">
                    {l.assigned_agent
                      ? <span className="inline-flex items-center gap-1 text-[10px] text-[#B38B2D]"><UserCheck className="h-3 w-3" /> {l.assigned_agent.name}</span>
                      : <span className="text-[10px] text-[#B9ADA4]">Unassigned</span>}
                    <button onClick={() => setOpen(open === l.id ? null : l.id)} data-testid={`re-board-manage-${l.id}`} className="text-[10px] font-medium text-[#5A504B] hover:text-[#1A1615]">Manage</button>
                  </div>
                  {open === l.id && <LeadDetail lead={l} agents={agents} onChange={onChange} />}
                </div>
              ))}
              {!col.length && <p className="rounded-xl border border-dashed border-[#E5D9CF] px-2 py-4 text-center text-[10px] font-light text-[#B9ADA4]">Drop a lead here</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
};

/* ---------------- List view ---------------- */
const ListView = ({ leads, stages, agents, onStage, onChange }) => {
  const [expanded, setExpanded] = useState(null);
  return (
    <div className="mt-5 space-y-3">
      {leads.map((l) => (
        <div key={l.id} className="glass rounded-2xl p-5" data-testid={`re-lead-${l.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#F8F3E6]"><Target className="h-4 w-4 text-[#B38B2D]" /></span>
                <div>
                  <p className="flex items-center gap-2 text-sm font-semibold text-[#1A1615]">{l.name} {l.needs_human && <NeedsHumanTag />}</p>
                  <p className="text-[11px] text-[#91837C]">{l.messages} messages · {fmt(l.updated_at)}</p>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#5A504B]">
                {l.phone && <a href={`tel:${l.phone}`} className="inline-flex items-center gap-1 hover:text-[#B38B2D]"><Phone className="h-3 w-3" /> {l.phone}</a>}
                {l.email && <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1 hover:text-[#B38B2D]"><Mail className="h-3 w-3" /> {l.email}</a>}
                {l.assigned_agent && <span className="inline-flex items-center gap-1 text-[#B38B2D]"><UserCheck className="h-3 w-3" /> {l.assigned_agent.name}</span>}
              </div>
              <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
                {chips(l).map(([k, v]) => (
                  <span key={k} className="rounded-full bg-white px-3 py-1 text-[#5A504B]"><b className="text-[#1A1615]">{k}</b> {String(v)}</span>
                ))}
              </div>
              {(l.stage === "won" || l.stage === "lost") && <OutcomeTag stage={l.stage} reason={l.outcome_reason} />}
            </div>
            <div className="flex flex-col items-end gap-2">
              <span className={`rounded-full px-2.5 py-1 text-[9px] font-bold uppercase tracking-[0.12em] ${STAGE_COLORS[l.stage] || STAGE_COLORS.new}`}>{l.stage}</span>
              <button onClick={() => setExpanded(expanded === l.id ? null : l.id)} data-testid={`re-lead-expand-${l.id}`} className="inline-flex items-center gap-1 text-[11px] font-medium text-[#5A504B] hover:text-[#1A1615]">
                Manage <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expanded === l.id ? "rotate-180" : ""}`} />
              </button>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2 border-t border-[#EBE3DA] pt-4">
            {stages.map((s) => (
              <button key={s} onClick={() => onStage(l.id, s)} data-testid={`re-lead-${l.id}-stage-${s}`}
                className={`rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.1em] transition-colors ${l.stage === s ? "bg-[#1A1615] text-[#F3E5AB]" : "border border-[#E5D9CF] text-[#5A504B] hover:bg-[#F5EBE6]"}`}>
                {s}
              </button>
            ))}
          </div>
          {expanded === l.id && <LeadDetail lead={l} agents={agents} onChange={onChange} />}
        </div>
      ))}
      {!leads.length && <p className="glass rounded-2xl p-6 text-sm font-light text-[#91837C]">No leads yet — they appear here the moment Sara captures a name and phone.</p>}
    </div>
  );
};

const tally = (rows) => {
  const m = {};
  rows.forEach((l) => { const k = (l.outcome_reason || "").trim() || "No reason given"; m[k] = (m[k] || 0) + 1; });
  return Object.entries(m).sort((a, b) => b[1] - a[1]);
};

const ReasonBars = ({ rows, accent, empty }) => {
  const data = tally(rows);
  const max = Math.max(1, ...data.map(([, n]) => n));
  if (!rows.length) return <p className="text-xs font-light text-[#B9ADA4]">{empty}</p>;
  return (
    <div className="space-y-2">
      {data.map(([reason, n]) => (
        <div key={reason} data-testid={`re-insight-row-${reason.replace(/\s+/g, "-").toLowerCase()}`}>
          <div className="mb-0.5 flex items-center justify-between text-[11px]">
            <span className="truncate text-[#5A504B]">{reason}</span>
            <span className="font-semibold text-[#1A1615]">{n}</span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-[#EFE7DC]">
            <div className={`h-full rounded-full ${accent}`} style={{ width: `${(n / max) * 100}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
};

const InsightsPanel = ({ leads }) => {
  const won = leads.filter((l) => l.stage === "won");
  const lost = leads.filter((l) => l.stage === "lost");
  const closed = won.length + lost.length;
  const winRate = closed ? Math.round((won.length / closed) * 100) : 0;
  return (
    <div className="glass mt-4 rounded-2xl p-5" data-testid="re-leads-insights">
      <div className="mb-4 flex flex-wrap items-center gap-x-6 gap-y-1">
        <p className="font-serif text-base font-semibold text-[#1A1615]">Outcome insights</p>
        <p className="text-xs text-[#91837C]">
          Win rate <b className="text-emerald-600">{winRate}%</b> · <b className="text-[#1A1615]">{won.length}</b> won · <b className="text-[#1A1615]">{lost.length}</b> lost
        </p>
      </div>
      <div className="grid gap-6 sm:grid-cols-2">
        <div>
          <p className="mb-2 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-emerald-600"><Trophy className="h-3 w-3" /> Why we won</p>
          <ReasonBars rows={won} accent="bg-emerald-500" empty="No won deals yet." />
        </div>
        <div>
          <p className="mb-2 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-stone-500"><X className="h-3 w-3" /> Why we lost</p>
          <ReasonBars rows={lost} accent="bg-stone-400" empty="No lost deals yet." />
        </div>
      </div>
    </div>
  );
};


const exportCsv = (leads) => {
  const cols = [["Name", "name"], ["Phone", "phone"], ["Email", "email"], ["Stage", "stage"],
    ["Intent", "intent"], ["Community", "community"], ["Budget", "budget"], ["Bedrooms", "bedrooms"],
    ["Timeline", "timeline"], ["Needs Human", "needs_human"], ["Outcome Reason", "outcome_reason"], ["Messages", "messages"], ["Updated", "updated_at"]];
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const rows = [cols.map(([h]) => esc(h)).join(",")];
  leads.forEach((l) => rows.push(cols.map(([, k]) =>
    esc(k === "needs_human" ? (l[k] ? "YES" : "") : k === "assigned_agent" ? (l.assigned_agent?.name || "") : l[k])
  ).join(",")));
  const blob = new Blob(["\ufeff" + rows.join("\r\n")], { type: "text/csv;charset=utf-8;" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `docstate-leads-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
  toast.success("Leads exported — open in Excel");
};

export const ReLeadsTab = () => {
  const [data, setData] = useState(null);
  const [view, setView] = useState("board");
  const [outcome, setOutcome] = useState(null); // { id, stage } when asking for a win/lost reason
  const [showInsights, setShowInsights] = useState(false);

  const load = () => api.get("/admin/realestate/leads").then((r) => setData(r.data)).catch(() => setData({ leads: [], stages: [], agents: [] }));
  useEffect(() => { load(); }, []);

  const commitStage = async (id, stage, reason) => {
    setData((d) => d && ({ ...d, leads: d.leads.map((l) => (l.id === id ? { ...l, stage, outcome_reason: ["won", "lost"].includes(stage) ? reason : "" } : l)) }));
    try { await api.patch(`/admin/realestate/leads/${id}`, { stage, ...(reason !== undefined ? { reason } : {}) }); await load(); toast.success(`Moved to ${stage}`); }
    catch { toast.error("Update failed"); load(); }
  };

  const setStage = (id, stage) => {
    const current = (data?.leads || []).find((l) => l.id === id);
    if (current && current.stage === stage) return;
    if (stage === "won" || stage === "lost") { setOutcome({ id, stage }); return; }
    commitStage(id, stage);
  };

  if (data === null) return <div className="flex min-h-[40vh] items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#C9A24B]" /></div>;

  const stages = data.stages || [];
  const agents = data.agents || [];
  const leads = data.leads || [];
  const needHuman = leads.filter((l) => l.needs_human).length;
  const won = leads.filter((l) => l.stage === "won").length;
  const open = leads.filter((l) => !["won", "lost"].includes(l.stage)).length;

  return (
    <div data-testid="re-leads-tab">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-serif text-2xl font-semibold text-[#1A1615]">Leads CRM</h2>
          <p className="mt-1 text-xs text-[#91837C]">
            {leads.length} leads · <b className="text-[#1A1615]">{open}</b> open to close · <b className="text-emerald-600">{won}</b> won
            {needHuman > 0 && <> · <b className="text-rose-600">{needHuman} need a human</b></>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-full bg-white p-0.5 shadow-sm">
            <button onClick={() => setView("board")} data-testid="re-leads-view-board"
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold transition-colors ${view === "board" ? "bg-[#1A1615] text-[#D4AF37]" : "text-[#91837C] hover:text-[#1A1615]"}`}>
              <Columns3 className="h-3.5 w-3.5" /> Board
            </button>
            <button onClick={() => setView("list")} data-testid="re-leads-view-list"
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold transition-colors ${view === "list" ? "bg-[#1A1615] text-[#D4AF37]" : "text-[#91837C] hover:text-[#1A1615]"}`}>
              <List className="h-3.5 w-3.5" /> List
            </button>
          </div>
          <button onClick={() => setShowInsights((v) => !v)} data-testid="re-leads-insights-toggle"
            className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-[11px] font-semibold transition-colors ${showInsights ? "border-[#1A1615] bg-[#1A1615] text-[#D4AF37]" : "border-[#E5D9CF] bg-white text-[#5A504B] hover:bg-[#F5EBE6]"}`}>
            <BarChart3 className="h-3.5 w-3.5" /> Insights
          </button>
          <button onClick={() => exportCsv(leads)} data-testid="re-leads-export"
            className="inline-flex items-center gap-1.5 rounded-full border border-[#E5D9CF] bg-white px-3.5 py-2 text-[11px] font-semibold text-[#5A504B] transition-colors hover:bg-[#F5EBE6]">
            <Download className="h-3.5 w-3.5" /> Excel
          </button>
        </div>
      </div>

      {showInsights && <InsightsPanel leads={leads} />}

      {view === "board"
        ? <Board leads={leads} stages={stages} agents={agents} onStage={setStage} onChange={load} />
        : <ListView leads={leads} stages={stages} agents={agents} onStage={setStage} onChange={load} />}

      {outcome && (
        <ReasonModal
          stage={outcome.stage}
          onClose={() => setOutcome(null)}
          onSave={(reason) => { commitStage(outcome.id, outcome.stage, reason); setOutcome(null); }}
        />
      )}
    </div>
  );
};
